import type {
  CopilotContext,
  CopilotContextDataEntry,
} from "src/clients/agent-management";
import {
  CONTEXTUAL_ENTITY_DEFINITIONS,
  REFERENCE_CONTEXTUAL_ENTITY_TYPES,
} from "src/unifiedCopilot/context/entityDefinitions";

/** The resource types this client renders as links. */
export const CLIENT_RESOURCE_TYPES_CONTEXT_KEY = "client_resource_types";

/** What each advertised capability means. */
export const CLIENT_CAPABILITIES_CONTEXT_KEY = "client_capabilities";

const CLIENT_RESOURCE_TYPES_CAPABILITY =
  "Resource types this client renders as links. Reference one with its exact " +
  "type string to turn it into a link the user can follow; a type listed with " +
  "a parentType must be referenced together with that parent.";

/**
 * A type the client can link: the parent it is addressable within, and
 * whatever else the agent needs to address one.
 */
export type ClientResourceType = {
  type: string;
  parentType?: string;
  description?: string;
};

export const clientResourceTypes = (): ClientResourceType[] =>
  REFERENCE_CONTEXTUAL_ENTITY_TYPES.flatMap((type) => {
    const { deeplink, parentType, description } =
      CONTEXTUAL_ENTITY_DEFINITIONS[type];
    // A type with no deeplink yet is not offered as a link target.
    return deeplink === undefined
      ? []
      : [
          {
            type,
            ...(parentType ? { parentType } : {}),
            ...(description ? { description } : {}),
          },
        ];
  });

/**
 * What this client can do with resources, so the agent's instructions need no
 * copy of the vocabulary: the linkable types, and what that list means.
 */
export const clientAdvertisement = (): CopilotContextDataEntry[] => [
  {
    key: CLIENT_RESOURCE_TYPES_CONTEXT_KEY,
    value: clientResourceTypes(),
  },
  {
    key: CLIENT_CAPABILITIES_CONTEXT_KEY,
    value: {
      [CLIENT_RESOURCE_TYPES_CONTEXT_KEY]: CLIENT_RESOURCE_TYPES_CAPABILITY,
    },
  },
];

/** Appends the advertisement to a request's context. */
export const withClientAdvertisement = (
  context: CopilotContext,
): CopilotContext => ({
  ...context,
  data: [...(context.data ?? []), ...clientAdvertisement()],
});
